import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { batchAPI } from '../services/api';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { Building2, Package, Image as ImageIcon, CheckCircle, XCircle, TrendingUp, Eye, Upload, Trash2, AlertTriangle, PackageCheck, PackageX, Ban } from 'lucide-react';

const TenantAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({
    totalBatches: 0,
    totalImages: 0,
    passedImages: 0,
    failedImages: 0,
  });
  const [batchProgress, setBatchProgress] = useState({}); // Track validation progress for each batch

  useEffect(() => {
    fetchBatches(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Poll validation progress for processing batches
  useEffect(() => {
    const processingBatches = batches.filter(b => b.status === 'processing');

    if (processingBatches.length === 0) return;

    const fetchProgress = async () => {
      for (const batch of processingBatches) {
        try {
          const response = await batchAPI.getValidationProgress(batch.batch_id);
          setBatchProgress(prev => ({
            ...prev,
            [batch.batch_id]: response.data
          }));

          // If validation is complete, refresh batches
          if (response.data.current_status === 'waiting' || response.data.current_status === 'errors') {
            fetchBatches(activeTab);
          }
        } catch (error) {
          console.error(`Error fetching progress for batch ${batch.batch_id}:`, error);
        }
      }
    };

    // Initial fetch
    fetchProgress();

    // Poll every 2 seconds
    const interval = setInterval(fetchProgress, 2000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches, activeTab]);

  const fetchBatches = async (filterTab = 'all') => {
    try {
      setLoading(true);
      const response = await batchAPI.list(0, 100, filterTab);
      const batchList = response.data;
      setBatches(batchList);

      const totalImages = batchList.reduce((sum, b) => sum + b.total_images, 0);
      const passedImages = batchList.reduce((sum, b) => sum + b.passed_images, 0);
      const failedImages = batchList.reduce((sum, b) => sum + b.failed_images, 0);

      setStats({
        totalBatches: batchList.length,
        totalImages,
        passedImages,
        failedImages,
      });
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId, batchName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${batchName}"?\n\nThis action cannot be undone and will permanently remove all batch data and images.`
    );

    if (!confirmDelete) return;

    try {
      await batchAPI.delete(batchId);
      const updatedBatches = batches.filter(batch => batch.batch_id !== batchId);
      setBatches(updatedBatches);

      const totalImages = updatedBatches.reduce((sum, b) => sum + b.total_images, 0);
      const passedImages = updatedBatches.reduce((sum, b) => sum + b.passed_images, 0);
      const failedImages = updatedBatches.reduce((sum, b) => sum + b.failed_images, 0);

      setStats({
        totalBatches: updatedBatches.length,
        totalImages,
        passedImages,
        failedImages,
      });

      alert(`Batch "${batchName}" has been successfully deleted.`);
    } catch (error) {
      console.error('Error deleting batch:', error);
      alert(`Failed to delete batch "${batchName}". Please try again.`);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .page-actions {
      display: flex;
      gap: 12px;
      margin-bottom: 32px;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 20px;
      margin-bottom: 32px;
    }

    .tabs-container {
      display: flex;
      gap: 8px;
      margin-bottom: 32px;
      border-bottom: 2px solid hsl(var(--border));
      padding-bottom: 0;
    }

    .tab-button {
      padding: 12px 24px;
      background: transparent;
      border: none;
      border-bottom: 2px solid transparent;
      font-size: 14px;
      font-weight: 600;
      color: hsl(var(--muted-foreground));
      cursor: pointer;
      transition: all 0.2s;
      margin-bottom: -2px;
    }

    .tab-button:hover {
      color: hsl(var(--foreground));
    }

    .tab-button.active {
      color: hsl(var(--primary));
      border-bottom-color: hsl(var(--primary));
    }

    .content-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      overflow: hidden;
    }

    .card-header {
      padding: 20px 24px;
      border-bottom: 1px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .card-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--foreground));
    }

    .batches-list {
      padding: 24px;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
      gap: 20px;
      max-height: none;
    }

    .batch-item {
      padding: 24px;
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-lg);
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      background: var(--gradient-card);
      display: flex;
      flex-direction: column;
      gap: 16px;
      box-shadow: var(--shadow-card);
    }

    .batch-item:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-card-hover);
      border-color: hsl(var(--primary) / 0.5);
    }

    .batch-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      padding-bottom: 16px;
      border-bottom: 1px solid hsl(var(--border));
    }

    .batch-info {
      flex: 1;
      min-width: 0;
    }

    .batch-title {
      font-size: 16px;
      font-weight: 700;
      color: hsl(var(--foreground));
      margin: 0 0 8px 0;
      word-break: break-word;
    }

    .batch-meta {
      font-size: 12px;
      color: hsl(var(--muted-foreground));
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .batch-status-section {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px 0;
      border-bottom: 1px solid hsl(var(--border));
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
    }

    .empty-icon {
      font-size: 48px;
      opacity: 0.3;
      margin-bottom: 16px;
    }

    .loading-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
    }

    .progress-container {
      width: 100%;
      height: 6px;
      background: hsl(var(--secondary));
      border-radius: 9999px;
      overflow: hidden;
    }

    .progress-bar {
      height: 100%;
      background: var(--gradient-primary);
      transition: width 0.3s;
      border-radius: 9999px;
    }

    .action-buttons {
      display: flex;
      gap: 8px;
    }

    .btn-small {
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 600;
      border-radius: var(--radius-md);
      border: none;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .btn-view {
      background: hsl(var(--info) / 0.1);
      color: hsl(var(--info));
      border: 1px solid hsl(var(--info) / 0.2);
    }

    .btn-view:hover {
      background: hsl(var(--info) / 0.2);
    }

    .btn-upload {
      background: hsl(var(--success) / 0.1);
      color: hsl(var(--success));
      border: 1px solid hsl(var(--success) / 0.2);
    }

    .btn-upload:hover {
      background: hsl(var(--success) / 0.2);
    }

    .btn-danger {
      background: hsl(var(--destructive) / 0.1);
      color: hsl(var(--destructive));
      border: 1px solid hsl(var(--destructive) / 0.2);
    }

    .btn-danger:hover {
      background: hsl(var(--destructive) / 0.2);
    }

    .alert {
      padding: 12px 16px;
      border-radius: var(--radius-md);
      margin-top: 12px;
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .alert-error {
      background: hsl(var(--destructive) / 0.1);
      border: 1px solid hsl(var(--destructive) / 0.3);
      color: hsl(var(--destructive));
    }

    .alert-icon {
      flex-shrink: 0;
    }
  `;

  if (loading && batches.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-container">
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              Loading Tenant Admin Dashboard...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <h1 className="page-title">
            <Building2 size={32} />
            Tenant Admin Dashboard
          </h1>
          <p className="page-subtitle">Manage users and batches for {user?.tenant_name || 'your organization'}</p>
        </div>


        {/* Stats */}
        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <Package size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '16px 0 4px' }}>
              {stats.totalBatches}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Batches
            </p>
            <div style={{ fontSize: '12px', color: 'hsl(var(--success))', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <TrendingUp size={14} />
              Active tenant
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <ImageIcon size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '16px 0 4px' }}>
              {stats.totalImages}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Images
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <CheckCircle size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--success))', margin: '16px 0 4px' }}>
              {stats.passedImages}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Passed Images
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <XCircle size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--destructive))', margin: '16px 0 4px' }}>
              {stats.failedImages}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Failed Images
            </p>
          </motion.div>
        </motion.div>

        {/* Tabs */}
        <div className="tabs-container">
          {[
            { key: 'all', label: 'All Batches', icon: Package },
            { key: 'passed', label: 'Passed', icon: PackageCheck },
            { key: 'failed', label: 'Failed', icon: PackageX },
            { key: 'rejected', label: 'Rejected', icon: Ban },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Batches */}
        <motion.div
          className="content-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="card-header">
            <h2 className="card-title">All Batches (Tenant View)</h2>
          </div>

          <div className="batches-list">
            {batches.length === 0 ? (
              <div className="empty-state">
                <Package size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>No batches found in your tenant.</p>
              </div>
            ) : (
              batches.map((batch) => (
                <div key={batch.batch_id} className="batch-item">
                  {/* Batch Header */}
                  <div className="batch-header">
                    <div className="batch-info">
                      <h3 className="batch-title">{batch.batch_name}</h3>
                      <div className="batch-meta">
                        <span>Uploaded: {new Date(batch.uploaded_at).toLocaleString()}</span>
                        {batch.uploaded_by_name && (
                          <span>By: {batch.uploaded_by_name}</span>
                        )}
                      </div>
                    </div>
                    <span className={`batch-status ${batch.status}`}>
                      {batch.status}
                    </span>
                  </div>

                  {/* Validation Status */}
                  <div className="batch-status-section">
                    {!['processing', 'pending'].includes(batch.status) && (
                    <span className={`batch-status ${batch.failed_images === 0 ? 'approved' : 'rejected'}`} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {batch.failed_images === 0 ? <><CheckCircle size={14} /> Validation Passed</> : <><XCircle size={14} /> Image Validation Failed</>}
                    </span>
                    )}
                    {batch.auditor_status === 'approved' && (
                      <span className="batch-status approved" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CheckCircle size={14} /> Auditor Approved
                      </span>
                    )}
                    {batch.auditor_status === 'rejected' && (
                      <span className="batch-status rejected" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Ban size={14} /> Auditor Rejected
                      </span>
                    )}
                    {batch.auditor_status === 'pending' && (
                      <span className="batch-status waiting" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AlertTriangle size={14} /> Pending Audit
                      </span>
                    )}
                  </div>

                      {/* GroupMetadata Validation Failures */}
                      {(batch.metadata_validation_failures || batch.artifact_count_match === false || batch.capture_id_match === false) && (
                        <div className="alert alert-error" style={{ marginTop: '12px' }}>
                          <AlertTriangle size={20} className="alert-icon" />
                          <div>
                            <strong>GROUPMETADATA VALIDATION FAILED:</strong>
                            <div>
                              {batch.metadata_validation_failures}
                              {batch.metadata_validation_failures && (batch.artifact_count_match === false || batch.capture_id_match === false) && ' '}
                              {batch.artifact_count_match === false && 'Total Artifacts: Count was Mismatch'}
                              {batch.artifact_count_match === false && batch.capture_id_match === false && ' '}
                              {batch.capture_id_match === false && 'Batch name and Capture ID is Mismatched'}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Image Sequence Validation Error */}
                      {batch.has_sequence_error === true && (
                        <div className="alert alert-error" style={{ marginTop: '12px' }}>
                          <AlertTriangle size={20} className="alert-icon" />
                          <div>
                            <strong>IMAGE SEQUENCE ERROR:</strong>
                            <div>Image numbers are not in sequence</div>
                          </div>
                        </div>
                      )}

                      {/* Rejection Reason */}
                      {batch.rejection_reason && (
                        <div className="alert alert-error" style={{ marginTop: '12px' }}>
                          <Ban size={20} className="alert-icon" />
                          <div>
                            <strong>Rejection Reason:</strong>
                            <div>{batch.rejection_reason}</div>
                          </div>
                        </div>
                      )}

                  {/* Validation Progress Bar */}
                  {batch.status === 'processing' && batchProgress[batch.batch_id] && (
                    <div style={{
                      padding: '16px',
                      background: 'hsl(var(--info) / 0.1)',
                      border: '1px solid hsl(var(--info) / 0.2)',
                      borderRadius: 'var(--radius-md)'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'hsl(var(--info))',
                        marginBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Package size={16} />
                        <span>Validating Images...</span>
                      </div>
                      <div className="progress-container">
                        <div
                          className="progress-bar"
                          style={{ width: `${batchProgress[batch.batch_id].progress_percentage}%` }}
                        />
                      </div>
                      <div style={{
                        marginTop: '10px',
                        fontSize: '12px',
                        color: 'hsl(var(--info))',
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <span>
                          Processed: {batchProgress[batch.batch_id].processed_images} / {batchProgress[batch.batch_id].total_images}
                        </span>
                        <span>
                          Passed: {batchProgress[batch.batch_id].passed_images} | Failed: {batchProgress[batch.batch_id].failed_images}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* No Images Message */}
                  {batch.total_images === 0 && batch.status === 'uploading' && (
                    <div style={{
                      padding: '16px',
                      background: 'hsl(var(--warning) / 0.1)',
                      border: '1px solid hsl(var(--warning) / 0.3)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <AlertTriangle size={20} style={{ color: 'hsl(var(--warning))' }} />
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--warning))' }}>
                          No Images Uploaded Yet
                        </div>
                        <div style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                          This batch is waiting for images to be uploaded.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Batch Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    padding: '16px',
                    background: 'hsl(var(--secondary) / 0.5)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: '16px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{
                        fontSize: '11px',
                        color: 'hsl(var(--muted-foreground))',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        margin: '0 0 4px 0'
                      }}>Total</p>
                      <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--foreground))', margin: 0 }}>
                        {batch.total_images || 0}
                      </h4>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{
                        fontSize: '11px',
                        color: 'hsl(var(--muted-foreground))',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        margin: '0 0 4px 0'
                      }}>Passed</p>
                      <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--success))', margin: 0 }}>
                        {batch.passed_images || 0}
                      </h4>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{
                        fontSize: '11px',
                        color: 'hsl(var(--muted-foreground))',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        margin: '0 0 4px 0'
                      }}>Failed</p>
                      <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--destructive))', margin: 0 }}>
                        {batch.failed_images || 0}
                      </h4>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{
                        fontSize: '11px',
                        color: 'hsl(var(--muted-foreground))',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        margin: '0 0 4px 0'
                      }}>Success Rate</p>
                      <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--info))', margin: 0 }}>
                        {batch.total_images > 0
                          ? Math.round((batch.passed_images / batch.total_images) * 100)
                          : 0}%
                      </h4>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button
                      onClick={() => navigate(`/batch/${batch.batch_id}`)}
                      className="btn-small btn-view"
                    >
                      <Eye size={16} />
                      <span>View Details</span>
                    </button>

                    {batch.status === 'incoming' && (
                      <button
                        onClick={() => navigate(`/batch/${batch.batch_id}/upload`)}
                        className="btn-small btn-upload"
                      >
                        <Upload size={16} />
                        <span>Upload Images</span>
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBatch(batch.batch_id, batch.batch_name);
                      }}
                      className="btn-small btn-danger"
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </DashboardLayout>
    </>
  );
};

export default TenantAdminDashboard;
